import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { ServerApiMapper } from '~commonInfra/utils/ServerModelHelper';
import { Messages } from '~src/codegen/Localize';
import { GetLinkedTaskActionsRequest } from '~src/flow/serverApi/requests/GetLinkedTaskActionsRequest';
import { FlowLinkedTaskAllowedActions } from '~src/flow/serverApi/types/FlowLinkedTaskAllowedActions';
import { isFlowSelfServiceSchedulesEnabled } from '~src/flow/utils/FlowSelfServiceSchedulesUtils';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { RequestHelper } from '~src/httpClient/utils/RequestHelper';
import { ResultHelper } from '~src/httpClient/utils/ResultHelper';
import WebHttpClient from '~src/httpClient/WebHttpClient';
import { WebHttpClientWithErrorToastInstance } from '~src/httpClient/WebHttpClientWithErrorToast';
import { LineageRelationshipType, RelatedContentType } from '~src/linkedTasks/models/ContentLineageRelationshipInfo';
import { CreateLinkedTaskRequest } from '~src/linkedTasks/serverApi/requests/CreateLinkedTaskRequest';
import { CreateLinkedTaskSelfServiceSchedulesRequest } from '~src/linkedTasks/serverApi/requests/CreateLinkedTaskSelfServiceSchedulesRequest';
import { DeleteLinkedTasksRequest } from '~src/linkedTasks/serverApi/requests/DeleteLinkedTasksRequest';
import { GetContentLineageRelationshipsRequest } from '~src/linkedTasks/serverApi/requests/GetContentLineageRelationshipsRequest';
import { GetLinkedTaskRequest, GetLinkedTaskRequestForServerRequest } from '~src/linkedTasks/serverApi/requests/GetLinkedTaskRequest';
import { ResumeLinkedTasksRequest } from '~src/linkedTasks/serverApi/requests/ResumeLinkedTasksRequest';
import { RunLinkedTasksRequest } from '~src/linkedTasks/serverApi/requests/RunLinkedTasksRequest';
import { UpdateLinkedTaskRequest } from '~src/linkedTasks/serverApi/requests/UpdateLinkedTaskRequest';
import { UpdateLinkedTaskSelfServiceSchedulesRequest } from '~src/linkedTasks/serverApi/requests/UpdateLinkedTaskSelfServiceSchedulesRequest';
import { UpdateLinkedTasksPriorityRequest } from '~src/linkedTasks/serverApi/requests/UpdateLinkedTasksPriorityRequest';
import { UpdateLinkedTasksScheduleRequest } from '~src/linkedTasks/serverApi/requests/UpdateLinkedTasksScheduleRequest';
import { LinkedTaskItem } from '~src/linkedTasks/serverApi/types/LinkedTaskItem';
import { SelfServiceSchedule } from '~src/schedule/serverApi/types/SelfServiceSchedule';

import { UpdateSelfServiceScheduleForLinkedTasksRequest } from '../serverApi/requests/UpdateSelfServiceScheduleForLinkedTasksRequest';
import { displayLinkedTaskToasts } from '../utils/LinkedTaskHelpers';

export class LinkedTasksResource {
  public createLinkedTask(scheduleId: string, schedule: SelfServiceSchedule, linkedTaskItems: LinkedTaskItem[]): Promise<IResult> {
    if(isFlowSelfServiceSchedulesEnabled()) {
      return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(
        new CreateLinkedTaskSelfServiceSchedulesRequest({
          schedule,
          linkedTaskItems,
        }));
    }

    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(
      new CreateLinkedTaskRequest({
        scheduleId,
        linkedTaskItems,
      }));
  }

  public actions(id: string): Promise<FlowLinkedTaskAllowedActions> {
    return WebHttpClient.sendRequest(new GetLinkedTaskActionsRequest(id));
  }

  public updateLinkedTask(id: string, scheduleId: string, schedule: SelfServiceSchedule, linkedTaskItems: LinkedTaskItem[]): Promise<IResult> {
    if(isFlowSelfServiceSchedulesEnabled()) {
      return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(
        new UpdateLinkedTaskSelfServiceSchedulesRequest({
          id,
          schedule,
          linkedTaskItems,
        }));
    }

    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(
      new UpdateLinkedTaskRequest({
        id,
        scheduleId,
        linkedTaskItems,
      }));
  }

  public getLinkedTask(id: string) {
    return WebHttpClient.sendRequest(new GetLinkedTaskRequest({id}))
      .then((result) => {
        if (result.errors) {
          displayLinkedTaskToasts(result);
        }
        return ServerApiMapper.linkedTask(result);
      });
  }

  public getLinkedTaskForServer(id: string) {
    return WebHttpClient.sendRequest(new GetLinkedTaskRequestForServerRequest({id}))
      .then((result) => {
        if (result.errors) {
          displayLinkedTaskToasts(result);
        }
        return ServerApiMapper.linkedTask(result);
      });
  }

  public deleteLinkedTasks(ids: string[]): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new DeleteLinkedTasksRequest({ids: idsBlock})),
      ResultHelper.combineResults
    );
  }

  public resumeLinkedTasks(ids: string[]): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new ResumeLinkedTasksRequest({ids: idsBlock})),
      ResultHelper.combineResults
    );
  }

  public runLinkedTasks(ids: string[]): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new RunLinkedTasksRequest({ids: idsBlock})),
      ResultHelper.combineResults
    );
  }

  public updateLinkedTasksSchedule(ids: string[], scheduleId: string, selfServiceSchedule: SelfServiceSchedule): Promise<IResult> {
    if(isFlowSelfServiceSchedulesEnabled()) {
      return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
        RequestHelper.buildRequests(ids, (idsBlock) => new UpdateSelfServiceScheduleForLinkedTasksRequest({ids: idsBlock, schedule: selfServiceSchedule})),
        ResultHelper.combineResults
      );
    }

    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new UpdateLinkedTasksScheduleRequest({ids: idsBlock, scheduleId})),
      ResultHelper.combineResults
    );
  }

  public setLinkedTasksPriority(ids: string[], priority: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new UpdateLinkedTasksPriorityRequest({ids: idsBlock, priority: priority})),
      ResultHelper.combineResults
    );
  }

  public getContentLineageRelationships(id: string, contentType: RelatedContentType, lineageRelationshipType: LineageRelationshipType) {
    return WebHttpClient.sendRequest(new GetContentLineageRelationshipsRequest({id: id, contentType: contentType, lineageRelationshipType: lineageRelationshipType}))
      .then((result) => {
        if(result.backfillRunning) {
          ToasterService.instance.pop({
            type: ToastType.Warning,
            text: Messages.linkedTaskLineageUnavailable(),
          });
        }
        if(result.errors) {
          return result;
        }
        return ServerApiMapper.contentLineageRelationships(result);
      });
  }
}

export const LinkedTasks = new LinkedTasksResource();
